import BenchlingConfig from 'BenchlingConfig';
import {
  ANTIBODY_CDR_ANNOTATIONS,
  ANTIBODY_NUMBERING,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';

import {CDRDefinition, NumberingScheme} from './AntibodyEngineeringConstants';

/**
 * Returns true when the legacy Mol Bio antibody tools (numbering, CDR annotations, PTM sites)
 * are enabled by the config flag. Per-entity suppression (e.g. hiding these tools on AA domain
 * entities when Biologics is enabled) is handled at the tab-state level — see
 * AaSequenceMapTabState.initialize().
 */
function areLegacyAntibodyToolsEnabled(): boolean {
  return BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS;
}

export function shouldShowNumbering(visibilityOptions: VisibilityOptions): boolean {
  return (
    areLegacyAntibodyToolsEnabled() &&
    Boolean(visibilityOptions.getOption(ANTIBODY_NUMBERING.name)?.get('visible'))
  );
}

export function shouldShowCDRs(visibilityOptions: VisibilityOptions): boolean {
  return (
    areLegacyAntibodyToolsEnabled() &&
    Boolean(visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name)?.get('visible'))
  );
}

export function shouldShowAntibodyTools(visibilityOptions: VisibilityOptions): boolean {
  return shouldShowNumbering(visibilityOptions) || shouldShowCDRs(visibilityOptions);
}

export function getAntibodyVisibilitySettings(
  visibilityOptions: VisibilityOptions
): [NumberingScheme | undefined, CDRDefinition | undefined] {
  if (!shouldShowAntibodyTools(visibilityOptions)) {
    return [undefined, undefined];
  }

  let numberingSetting;
  if (visibilityOptions.getOption(ANTIBODY_NUMBERING.name)?.get('visible')) {
    numberingSetting = visibilityOptions
      .getOption(ANTIBODY_NUMBERING.name)!
      .getSetting('numberingScheme')!
      .get('value')
      .toLowerCase();
  }

  let cdrSetting;
  if (visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name)?.get('visible')) {
    cdrSetting = visibilityOptions
      .getOption(ANTIBODY_CDR_ANNOTATIONS.name)!
      .getSetting('CDRDefinition')!
      .get('value')
      .toLowerCase();
  }
  return [numberingSetting, cdrSetting];
}

export function listenToAntibodyVisibilitySettings(
  visibilityOptions: VisibilityOptions,
  eventable: Backbone.Events,
  callbackFn: () => void
): void {
  eventable.listenTo(visibilityOptions.getOption(ANTIBODY_NUMBERING.name), 'change:visible', callbackFn);
  eventable.listenTo(
    visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name),
    'change:visible',
    callbackFn
  );
  eventable.listenTo(
    visibilityOptions.getOption(ANTIBODY_NUMBERING.name)!.getSetting('numberingScheme'),
    'change:value',
    callbackFn
  );
  eventable.listenTo(
    visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name)!.getSetting('CDRDefinition'),
    'change:value',
    callbackFn
  );
}
